import { ref } from "vue";
import { customApi } from "@app/helpers";

/**
 * Reactive state for WordPress login status.
 * @type {import('vue').Ref<boolean>}
 */
export const isLoggedIn = ref(false);

/**
 * Reactive state for the current WordPress user's data.
 * @type {import('vue').Ref<Object|null>}
 */
export const currentUser = ref(null);

/**
 * Reactive state for the WordPress REST API Nonce.
 * Initialized from window.wpApiSettings when this module is loaded.
 */
export const wpRestNonce = ref(window.wpApiSettings?.nonce || null);

/**
 * Checks the current WordPress user's login status by calling the custom REST API endpoint.
 * Updates the reactive isLoggedIn and currentUser variables.
 *
 * @async
 * @returns {Promise<{loggedIn: boolean, user: Object|null}>} A promise that resolves with the login status and user data.
 * @throws {Error} If the API request fails or returns an unexpected response.
 */
export async function checkWordPressLoginStatus() {
  try {
    const response = await fetch("/wp-json/custom/v1/user/status", {
      method: "GET",
      credentials: "same-origin", // Correct for same-origin requests
      headers: {
        "Content-Type": "application/json",
        "X-WP-Nonce": window.wpApiSettings?.nonce,
      },
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += `, Message: ${
          errorData.message || JSON.stringify(errorData)
        }`;
      } catch (e) {
        // Failed to parse error JSON
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();

    if (typeof data.loggedIn === "boolean") {
      isLoggedIn.value = data.loggedIn;
      currentUser.value = data.user || null;

      if (data.loggedIn && data.user) {
        console.log(
          "User is logged in to WordPress (via authService):",
          currentUser.value
        );
        // Optionally, sync/update localStorage if you use it for non-critical UI hints
        localStorage.setItem("userEmail", data.user.email);
      } else {
        console.log("User is not logged in to WordPress (via authService).");
        localStorage.removeItem("userEmail"); // Clear if not logged in
      }
      return { loggedIn: isLoggedIn.value, user: currentUser.value };
    } else {
      throw new Error("Invalid response structure from /user/status endpoint.");
    }
  } catch (error) {
    console.error("Error checking WordPress login status:", error);
    isLoggedIn.value = false;
    currentUser.value = null;
    localStorage.removeItem("userEmail"); // Ensure cleanup on error
    throw error; // Re-throw the error so the caller can handle it
  }
}

/**
 * Attempts to log in the user with the provided credentials via the custom WordPress REST API endpoint.
 * Updates reactive state (isLoggedIn, currentUser) upon success or failure.
 *
 * @async
 * @param {string} username - The user's username.
 * @param {string} password - The user's password.
 * @param {boolean} [remember=false] - Whether to set a persistent cookie.
 * @returns {Promise<{success: boolean, loggedIn: boolean, user: Object|null, message?: string}>}
 *          A promise that resolves with the outcome of the login attempt.
 * @throws {Error} If the API request itself fails network-wise or returns an unhandled server error.
 */
export async function loginWithCredentials(
  username,
  password,
  remember = false
) {
  try {
    const response = await fetch("/wp-json/custom/v1/login", {
      method: "POST",
      credentials: "same-origin", // Correct for same-origin requests
      headers: {
        "Content-Type": "application/json",
        // Nonce might also be required for login if your server/WP setup is strict
        "X-WP-Nonce": window.wpApiSettings?.nonce,
      },
      body: JSON.stringify({ username, password, remember }),
    });

    const data = await response.json();

    if (!response.ok) {
      // The server responded with an error status (4xx, 5xx)
      // data.message should contain the error from WP_Error
      console.error(
        "Login failed (server error):",
        data.message || `HTTP ${response.status}`
      );
      isLoggedIn.value = false;
      currentUser.value = null;
      localStorage.removeItem("userEmail");
      return {
        success: false,
        loggedIn: false,
        user: null,
        message: data.message || `Login failed with status: ${response.status}`,
      };
    }

    // Assuming successful login returns the same structure as /user/status
    if (typeof data.loggedIn === "boolean" && data.loggedIn) {
      isLoggedIn.value = data.loggedIn;
      currentUser.value = data.user || null;
      localStorage.setItem("userEmail", data.user.email); // Consistent with checkWordPressLoginStatus
      console.log(
        "User successfully logged in (via authService):",
        currentUser.value
      );
      return { success: true, loggedIn: true, user: currentUser.value };
    } else {
      // Should not happen if wp_signon was successful and tj_get_current_user_status was called
      throw new Error("Login succeeded but received invalid user status data.");
    }
  } catch (error) {
    console.error("Error during loginWithCredentials:", error);
    isLoggedIn.value = false;
    currentUser.value = null;
    localStorage.removeItem("userEmail");
    throw error; // Re-throw for the calling component to handle if needed
  }
}

/**
 * Attempts to register a new user with the provided credentials via the custom WordPress REST API endpoint.
 *
 * @async
 * @param {string} email - The user's email address (will also be used as username).
 * @param {string} password - The user's desired password.
 * @param {string} mobileNumber - The user's mobile number.
 * @returns {Promise<{success: boolean, message?: string, data?: any}>}
 *          A promise that resolves with the outcome of the registration attempt.
 * @throws {Error} If the API request itself fails network-wise or returns an unhandled server error.
 */
export async function registerWithCredentials(email, password, mobileNumber) {
  try {
    const response = await fetch("/wp-json/custom/v1/register", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-WP-Nonce": window.wpApiSettings?.nonce, // Nonce for authenticated actions, good practice
      },
      body: JSON.stringify({ email, password, mobile_number: mobileNumber }), // Ensure key matches backend
    });

    const data = await response.json();

    if (!response.ok) {
      // The server responded with an error status (4xx, 5xx)
      // data.message should contain the error from WP_Error
      console.error(
        "Registration failed (server error):",
        data.message || `HTTP ${response.status}`
      );
      return {
        success: false,
        message:
          data.message || `Registration failed with status: ${response.status}`,
        data: data, // Include full error data if available
      };
    }

    // Assuming successful registration returns { success: true, message: "..." }
    if (data.success) {
      console.log("User successfully registered (via authService):", email);
      return { success: true, message: data.message };
    } else {
      // Should not happen if response.ok was true and backend returns {success: true}
      throw new Error(
        data.message ||
          "Registration succeeded but received invalid response data."
      );
    }
  } catch (error) {
    console.error("Error during registerWithCredentials:", error);
    throw error; // Re-throw for the calling component to handle if needed
  }
}

/**
 * Attempts to update the currently logged-in user's password.
 *
 * @async
 * @param {string} currentPassword - The user's current password.
 * @param {string} newPassword - The user's desired new password.
 * @returns {Promise<{success: boolean, message?: string}>}
 *          A promise that resolves with the outcome of the password update attempt.
 */
export async function updateUserPassword(currentPassword, newPassword) {
  if (!wpRestNonce.value) {
    console.error("WP REST Nonce is not available for password update.");
    return {
      success: false,
      message: "Authentication error. Please refresh and try again.",
    };
  }

  try {
    const response = await fetch("/wp-json/custom/v1/user/update-password", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-WP-Nonce": wpRestNonce.value,
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error(
        "Password update failed (server error):",
        data.message || `HTTP ${response.status}`
      );
      return {
        success: false,
        message:
          data.message ||
          `Password update failed with status: ${response.status}`,
      };
    }

    return {
      success: data.success,
      message: data.message || "Password updated successfully.",
    };
  } catch (error) {
    console.error("Error during updateUserPassword:", error);
    return {
      success: false,
      message:
        "An unexpected error occurred while updating your password. Please try again.",
    };
  }
}

/**
 * Sends a password reset request to the WordPress backend.
 *
 * @async
 * @param {string} email - The user's email address for whom the password reset is requested.
 * @returns {Promise<{success: boolean, message?: string}>}
 *          A promise that resolves with the outcome of the password reset request.
 */
export async function sendPasswordResetEmail(email) {
  if (!wpRestNonce.value) {
    // While retrieve_password itself doesn't always need a nonce for public requests,
    // it's good practice if your endpoint is part of a system that generally uses nonces.
    // For a public "forgot password" feature, a nonce might be less critical here than for authenticated actions.
    // However, if your WP setup is strict, include it.
    console.warn(
      "WP REST Nonce is not available for password reset request. Proceeding without it for this public action."
    );
  }

  try {
    const response = await fetch(
      "/wp-json/custom/v1/user/request-password-reset",
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          // "X-WP-Nonce": wpRestNonce.value, // Include if your endpoint requires it
        },
        body: JSON.stringify({ email: email }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      // Handles HTTP errors (4xx, 5xx)
      return {
        success: false,
        message:
          data.message || `Request failed with status: ${response.status}`,
      };
    }

    // Assumes backend returns { success: true/false, message: "..." }
    return { success: data.success, message: data.message };
  } catch (error) {
    console.error("Error during sendPasswordResetEmail:", error);
    return {
      success: false,
      message: "An unexpected network error occurred. Please try again.",
    };
  }
}

export const twilio = {
  /**
   * Twilio 2FA Send Verification
   */
  async sendToken(email) {
    try {
      const response = await customApi.post(`/twilio/send/${email}`);
      return {
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        status: error.response.status,
        message: error.response.data
          ? error.response.data.message
          : "Connection Error. If your internet is fine, then there is a server issue.",
      };
    }
  },

  /**
   * Twilio 2FA Token Verification
   */
  async verifyToken(code, mobile) {
    try {
      const response = await customApi.post(`/twilio/verify/`, {
        code,
        mobile,
      });
      return {
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        status: error.response.status,
        message: error.response.data
          ? error.response.data.message
          : "Connection Error. If your internet is fine, then there is a server issue.",
      };
    }
  },
};

/**
 * Call this function once when your Vue application initializes if you want to
 * check the login status globally as soon as the app loads.
 * For example, in your main.js or App.vue.
 */
// export async function initializeAuth() {
//   try {
//     await checkWordPressLoginStatus();
//   } catch (error) {
//     console.error("Failed to initialize authentication status:", error);
//   }
// }
